import {Injectable} from '@angular/core';
import {Observable} from 'rxjs/Observable';
import {TimerObservable} from 'rxjs/observable/TimerObservable';
import {Router, CanActivate} from '@angular/router';
import {CustomHttpService} from './custom-http.service';
import {User} from '@myModels/User/user';
import {HttpOptionsModel} from '@myModels/Shared/http-options';
import {EventService} from './event.service';
import {LocalStorageService} from './localstorage.service';
import {ReplaySubject} from 'rxjs/ReplaySubject';
import {BehaviorSubject} from 'rxjs/BehaviorSubject';
import {catchError, map, takeWhile} from 'rxjs/operators';
import {JackpotService} from '@myServices/jackpot.service';


@Injectable()
export class AuthenticationService implements CanActivate {
    public user: User;
    public isUserLoggedIn = false;
    public alive = false;
    public currentGame = 0;
    private interval = 5000;
    private readonly _isInitializationFinished: ReplaySubject<boolean>;
    private _qrCode: BehaviorSubject<string>;

    constructor(
        private customHttpService: CustomHttpService,
        private eventService: EventService,
        private router: Router,
        private localStorageService: LocalStorageService,
        private jackpotService: JackpotService
    ) {
        this._isInitializationFinished = new ReplaySubject<boolean>(1);
        this._qrCode = new BehaviorSubject(null);
    }

    public initSite(): Observable<any> {
        const postData = {
            game_id: this.currentGame
        };
        const httpOptionsModel = new HttpOptionsModel(
            '/init',
            postData,
            false,
            true
        );
        return this.customHttpService.post(httpOptionsModel).pipe(
            catchError((error) => {
                if (error.error.messages) {
                    throw new Error(error.error.messages[0]);
                }
                throw new Error('UNEXPECTED_ERROR');
            }), map((res: any) => {
                if (!res.status) {
                    const msg = res.messages.length > 0 ? res.messages[0] : 'UNEXPECTED_ERROR';
                    throw new Error(msg);
                }
                this.jackpotService.setJackpots(res.data.jackpots);
                this.jackpotService.setJackpotWins(res.data.jackpotWins);
                return res;
            })
        );
    }


    public login(username: string, password: string) {
        const postData = {
            identity: username,
            password
        };
        const httpOptions = new HttpOptionsModel('/login', postData, false, true);
        const loginService = this.customHttpService.post(httpOptions);
        return loginService.pipe(
            catchError((error) => {
                if (error.error.messages) {
                    throw new Error(error.error.messages[0]);
                }
                throw new Error('UNEXPECTED_ERROR');
            }), map((res: any) => {
                this.isUserLoggedIn = res.status;
                return res;
            })
        );
    }

    public register(username: string, email: string, password: string, qrCode: string ) {
        const postData = {
            username,
            email,
            password,
            qr_code: qrCode
        };
        const httpOptions = new HttpOptionsModel('/site-auth/register', postData, false, true);

        return this.customHttpService.post(httpOptions).pipe(
            catchError((error) => {
                if (error.error.messages) {
                    throw new Error(error.error.messages[0]);
                }
                throw new Error('UNEXPECTED_ERROR');
            }), map((res: any) => {
                return res;
            })
        );
    }

    public activateRegisteredUser(email: string, nonce: string) {
        const postData = {
            email,
            code: nonce
        };
        const httpOptions = new HttpOptionsModel('/site-auth/activate', postData, false, true);

        return this.customHttpService.post(httpOptions).pipe(
            catchError((error) => {
                if (error.error.messages) {
                    throw new Error(error.error.messages[0]);
                }
                throw new Error('UNEXPECTED_ERROR');
            }), map((res: any) => {
                return res;
            })
        );
    }

    public forgotPassword(email: string) {
        const postData = {
            email
        };
        const httpOptions = new HttpOptionsModel('/site-auth/forgot-password', postData, false, true);

        return this.customHttpService.post(httpOptions).pipe(
            catchError((error) => {
                if (error.error.messages) {
                    throw new Error(error.error.messages[0]);
                }
                throw new Error('UNEXPECTED_ERROR');
            }), map((res: any) => {
                return res;
            })
        );
    }

    public resetPassword(email: string, password: string, nonce: string) {
        const postData = {
            email,
            password,
            code: nonce
        };
        const httpOptions = new HttpOptionsModel('/site-auth/reset-password', postData, false, true);

        return this.customHttpService.post(httpOptions).pipe(
            catchError((error) => {
                if (error.error.messages) {
                    throw new Error(error.error.messages[0]);
                }
                throw new Error('UNEXPECTED_ERROR');
            }), map((res: any) => {
                return res;
            })
        );
    }

    public logout() {
        const httpOptions = new HttpOptionsModel('/logout', null, false, true);
        return this.customHttpService.post(httpOptions).pipe(
            catchError((error) => {
                if (error.error.messages) {
                    throw new Error(error.error.messages[0]);
                }
                throw new Error('UNEXPECTED_ERROR');
            }), map((res: any) => {
                if (!res.status) {
                    const msg = res.messages.length > 0 ? res.messages[0] : 'UNEXPECTED_ERROR';
                    throw new Error(msg);
                }
                return res;
            })
        );
    }

    public loginManager(user, willValidate) {
        if (!!user && !!user.id) {
            this.initializeUser(user);
        } else {
            this.killUser();
        }

        if (willValidate) {
            this.validateSite();
        }
    }

    public setToken(token) {
        if (!this.localStorageService.getItem('token')) {
            this.localStorageService.setItem('token', token);
            window.location.reload();
            return true;
        }
        this.localStorageService.setItem('token', token);
    }

    public initializeSite(): Promise<any> {
        return new Promise<any>((resolve, reject) => {
            this.initSite().subscribe(
                res => {
                    const token = res.token;
                    if (token) {
                        this.setToken(token);
                    }
                    this._isInitializationFinished.next(true);
                    this.loginManager(res.data.user, true);
                    resolve(res);
                },
                err => {
                    console.log(err);
                    reject(err);
                }
            );
        });
    }

    public getCurrentUser() {
        const user = this.localStorageService.getItem('USER_INFO');
        if (user) {
            return user;
        }
        return null;
    }

    public canActivate() {
        return this.isUserLoggedIn;
    }

    get isInitializationFinished(): ReplaySubject<boolean> {
        return this._isInitializationFinished;
    }

    private initializeUser(user) {
        const userModel = new User(user);
        if (JSON.stringify(this.user) === JSON.stringify(userModel)) {
            return;
        }
        this.user = userModel;
        this.eventService.onUserInfoChange(userModel);
        this.eventService.onAuthenticationStatusChange(true);
        this.alive = true;
    }

    private killUser() {
        this.user = null;
        this.eventService.onUserInfoChange(null);
        this.eventService.onAuthenticationStatusChange(false);
        if (this.alive) {
            this.alive = false;
            this.router.navigate(['/']);
            window.location.reload();
        }
    }


    private validateSite() {
        TimerObservable.create(0, this.interval)
            .pipe()
            .subscribe(() => {
                this.initSite().subscribe(res => {
                    this.loginManager(res.data.user, false);
                });
            });
    }

    get qrCode(): BehaviorSubject<string> {
        return this._qrCode;
    }

    set qrCodeValue(value: string) {
        if (this.qrCode.getValue() !== value) {
            this._qrCode.next(value);
        }
    }

    get qrCodeValue() {
        return this.qrCode.getValue();
    }
}



// WEBPACK FOOTER //
// C:/Users/beyn/Documents/GitLab/spinmatic-casino/src/app/Modules/Shared/services/authentication.service.ts